/**
 * Grouping for the AI usage tab. ps-main returns raw ledger rows, so every
 * total on the tab is computed here, in the browser.
 */

import type { AiUsageEvent } from "./types";

export interface UsageTotals {
  costUsd: number;
  events: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
}

export interface UsageGroup extends UsageTotals {
  key: string;
  label: string;
  /** A second line for the label, e.g. the email under an employee name. */
  detail: string | null;
  /** Cost by `feature`. */
  costByFeature: Record<string, number>;
  /** The model with the highest cost in the group, if any. */
  topModel: string | null;
  lastOccurredAt: string;
}

function emptyTotals(): UsageTotals {
  return {
    costUsd: 0,
    events: 0,
    inputTokens: 0,
    outputTokens: 0,
    cacheReadTokens: 0,
    cacheCreationTokens: 0,
  };
}

function add(totals: UsageTotals, event: AiUsageEvent) {
  totals.costUsd += event.costUsd;
  totals.events += 1;
  totals.inputTokens += event.inputTokens;
  totals.outputTokens += event.outputTokens;
  totals.cacheReadTokens += event.cacheReadTokens;
  totals.cacheCreationTokens += event.cacheCreationTokens;
}

export function totalsOf(events: AiUsageEvent[]): UsageTotals {
  const totals = emptyTotals();
  for (const event of events) add(totals, event);
  return totals;
}

/**
 * Group events by `keyOf`, most expensive group first. `labelOf` is read from
 * the first event of each group.
 */
export function groupBy(
  events: AiUsageEvent[],
  keyOf: (event: AiUsageEvent) => string,
  labelOf: (event: AiUsageEvent) => { label: string; detail?: string | null },
): UsageGroup[] {
  const groups = new Map<
    string,
    UsageGroup & { costByModel: Map<string, number> }
  >();

  for (const event of events) {
    const key = keyOf(event);
    let group = groups.get(key);
    if (!group) {
      const { label, detail } = labelOf(event);
      group = {
        ...emptyTotals(),
        key,
        label,
        detail: detail ?? null,
        costByFeature: {},
        topModel: null,
        lastOccurredAt: event.occurredAt,
        costByModel: new Map(),
      };
      groups.set(key, group);
    }
    add(group, event);
    group.costByFeature[event.feature] =
      (group.costByFeature[event.feature] ?? 0) + event.costUsd;
    if (event.model) {
      group.costByModel.set(
        event.model,
        (group.costByModel.get(event.model) ?? 0) + event.costUsd,
      );
    }
    if (event.occurredAt > group.lastOccurredAt) {
      group.lastOccurredAt = event.occurredAt;
    }
  }

  return [...groups.values()]
    .map(({ costByModel, ...group }) => {
      let topModel: string | null = null;
      let topCost = -1;
      for (const [model, cost] of costByModel) {
        if (cost > topCost) {
          topModel = model;
          topCost = cost;
        }
      }
      return { ...group, topModel };
    })
    .sort((a, b) => b.costUsd - a.costUsd || a.label.localeCompare(b.label));
}

export function bySubscriber(events: AiUsageEvent[]): UsageGroup[] {
  return groupBy(
    events,
    (event) => event.subscriberId,
    (event) => ({ label: event.subscriberName }),
  );
}

/** Key of the group for usage a public API key consumed. */
export const API_KEY_GROUP = "public-api-key";

/**
 * Group one subscriber's events by the employee who consumed them. Usage with
 * no user (a public API key) forms one group of its own.
 */
export function byEmployee(events: AiUsageEvent[]): UsageGroup[] {
  return groupBy(
    events,
    (event) => event.userId ?? API_KEY_GROUP,
    (event) =>
      event.userId
        ? {
            label: event.employeeName ?? event.userEmail ?? event.userId,
            detail: event.employeeName ? event.userEmail : null,
          }
        : { label: "Public API key" },
  );
}

export function byModel(events: AiUsageEvent[]): UsageGroup[] {
  return groupBy(
    events,
    (event) => event.model ?? event.provider,
    (event) => ({
      label: event.model ?? `${event.provider} (no model)`,
    }),
  );
}

/** UTC calendar day of an ISO timestamp, as YYYY-MM-DD. */
function utcDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/**
 * Cost per UTC day from the day of `since` through the day of `until`, with a
 * zero for each day that had no usage.
 */
export function dailyCost(
  events: AiUsageEvent[],
  since: Date,
  until: Date,
): { day: string; costUsd: number; events: number }[] {
  const days = new Map<string, { costUsd: number; events: number }>();
  const cursor = new Date(
    Date.UTC(since.getUTCFullYear(), since.getUTCMonth(), since.getUTCDate()),
  );
  const last = utcDay(until);
  while (utcDay(cursor) <= last) {
    days.set(utcDay(cursor), { costUsd: 0, events: 0 });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  for (const event of events) {
    const bucket = days.get(utcDay(new Date(event.occurredAt)));
    if (!bucket) continue;
    bucket.costUsd += event.costUsd;
    bucket.events += 1;
  }

  return [...days].map(([day, bucket]) => ({ day, ...bucket }));
}

/**
 * Round `max` up to a clean axis maximum, and give the ticks from zero to it.
 * 0.37 becomes 0.4 with ticks 0, 0.1, 0.2, 0.3, 0.4.
 */
export function niceTicks(max: number, count = 4): number[] {
  if (!(max > 0)) return [0];
  const rough = max / count;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step =
    [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= rough) ??
    10 * magnitude;
  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let i = 0; i * step <= top + step / 1000; i++) {
    ticks.push(Number((i * step).toPrecision(12)));
  }
  return ticks;
}
